import { useState } from 'react';
import { americanPhonetics, getVowels, getConsonants, getPhoneticCategoryName } from '../data/phonetics';
import { ttsService } from '../services/tts';

export default function PhoneticsPage() {
  const [selectedCategory, setSelectedCategory] = useState<'vowel' | 'consonant' | 'all'>('all');

  const handlePlayPhonetic = async (exampleWord: string) => {
    try {
      await ttsService.speakSlowly(exampleWord);
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  const getFilteredPhonetics = () => {
    if (selectedCategory === 'vowel') return getVowels();
    if (selectedCategory === 'consonant') return getConsonants();
    return americanPhonetics;
  };

  const phonetics = getFilteredPhonetics();

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">📖 音标学习</h1>
        <p className="text-gray-500">学习英语发音的基础符号</p>
      </div>

      {/* Category selector */}
      <div className="flex justify-center gap-3">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-6 py-3 rounded-2xl font-medium transition-all ${
            selectedCategory === 'all'
              ? 'bg-primary text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          全部音标
        </button>
        <button
          onClick={() => setSelectedCategory('vowel')}
          className={`px-6 py-3 rounded-2xl font-medium transition-all ${
            selectedCategory === 'vowel'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          元音 {getVowels().length}个
        </button>
        <button
          onClick={() => setSelectedCategory('consonant')}
          className={`px-6 py-3 rounded-2xl font-medium transition-all ${
            selectedCategory === 'consonant'
              ? 'bg-purple-500 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          辅音 {getConsonants().length}个
        </button>
      </div>

      {/* Phonetics grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {phonetics.map((phonetic, index) => (
          <div
            key={index}
            className="bg-white rounded-2xl p-4 shadow-soft card-hover"
          >
            <div className="text-center mb-3">
              <p className="text-3xl font-bold text-primary mb-1">
                {phonetic.symbol}
              </p>
              <p className="text-sm text-gray-500">
                {getPhoneticCategoryName(phonetic.category)}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-3">
              <p className="text-sm text-gray-700 text-center">
                例词：<span className="font-bold text-primary">{phonetic.exampleWord}</span>
              </p>
            </div>

            <button
              onClick={() => handlePlayPhonetic(phonetic.exampleWord)}
              className="w-full py-2 bg-blue-100 text-blue-700 rounded-xl font-medium hover:bg-blue-200 transition-colors"
            >
              🔊 播放例词
            </button>

            <p className="text-xs text-gray-500 text-center mt-2">
              {phonetic.description}
            </p>
          </div>
        ))}
      </div>

      {/* Learning tips */}
      <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-3xl p-6">
        <h3 className="font-bold text-gray-700 mb-3">💡 学习提示</h3>
        <div className="space-y-2 text-sm text-gray-600">
          <p>• <strong>元音</strong>：发声时气流不受阻碍，声音清晰响亮</p>
          <p>• <strong>辅音</strong>：发声时气流受到阻碍，声音较短</p>
          <p>• <strong>单元音</strong>：发音时口型不变</p>
          <p>• <strong>双元音</strong>：发音时口型变化（从一个音滑向另一个音）</p>
          <p>• 点击播放例词，仔细听发音的区别</p>
        </div>
      </div>

      {/* Phonetic guide */}
      <div className="bg-white rounded-3xl p-6 shadow-soft">
        <h3 className="font-bold text-gray-700 mb-4">📚 音标基础知识</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-primary mb-2">IPA 国际音标</h4>
            <p className="text-sm text-gray-600">
              International Phonetic Alphabet，国际通用的语音符号系统。
              英语使用IPA的一部分，称为英语音标。
            </p>
          </div>

          <div>
            <h4 className="font-medium text-primary mb-2">美式 vs 英式</h4>
            <p className="text-sm text-gray-600">
              美式英语（American English）和英式英语（British English）
              在部分元音的发音上有差异，如 /oʊ/ vs /əʊ/。
            </p>
          </div>

          <div>
            <h4 className="font-medium text-primary mb-2">学习建议</h4>
            <p className="text-sm text-gray-600">
              不要死记硬背，通过大量听力练习自然掌握。
              可以对比不同音标的例词，感受发音差异。
            </p>
          </div>

          <div>
            <h4 className="font-medium text-primary mb-2">实践方法</h4>
            <p className="text-sm text-gray-600">
              学习新单词时，注意查看音标，尝试自己拼读，
              再听标准发音对比，逐步提高准确性。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}